/* @wc-ignore-file */
import { Component, type ErrorInfo, type ReactNode } from "react";
import { captureBoundaryError } from "@/lib/sentry";

export interface ErrorFallbackProps {
	error: Error;
	resetErrorBoundary?: () => void;
	componentStack?: string;
}

interface ErrorBoundaryProps {
	children: ReactNode;
	/** Static fallback element to render when an error occurs */
	fallback?: ReactNode;
	/** Component to render when an error occurs (receives error props) */
	FallbackComponent?: React.ComponentType<ErrorFallbackProps>;
	/** Callback fired when an error is caught */
	onError?: (error: Error, errorInfo: ErrorInfo) => void;
	/** Callback fired when the error boundary resets */
	onReset?: () => void;
}

interface ErrorBoundaryState {
	hasError: boolean;
	error: Error | null;
	componentStack: string | null;
}

/**
 * React Error Boundary component that catches JavaScript errors in child components.
 *
 * @example
 * ```tsx
 * <ErrorBoundary FallbackComponent={ErrorFallback} onError={logError}>
 *   <App />
 * </ErrorBoundary>
 * ```
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = {
			hasError: false,
			error: null,
			componentStack: null,
		};
	}

	static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
		console.error("ErrorBoundary caught an error:", error, errorInfo);

		captureBoundaryError(error, errorInfo.componentStack ?? undefined);

		this.setState({ componentStack: errorInfo.componentStack ?? null });

		this.props.onError?.(error, errorInfo);
	}

	resetErrorBoundary = (): void => {
		this.props.onReset?.();
		this.setState({
			hasError: false,
			error: null,
			componentStack: null,
		});
	};

	render(): ReactNode {
		const { hasError, error, componentStack } = this.state;
		const { children, fallback, FallbackComponent } = this.props;

		if (hasError && error) {
			// Render FallbackComponent if provided
			if (FallbackComponent) {
				return (
					<FallbackComponent
						error={error}
						resetErrorBoundary={this.resetErrorBoundary}
						componentStack={componentStack ?? undefined}
					/>
				);
			}

			// Render static fallback if provided
			if (fallback !== undefined) {
				return fallback;
			}

			// Default minimal fallback
			return (
				<div
					role="alert"
					style={{
						padding: "20px",
						textAlign: "center",
						fontFamily: "system-ui, sans-serif",
					}}
				>
					<h1>Something went wrong</h1>
					<button type="button" onClick={this.resetErrorBoundary}>
						Try again
					</button>
				</div>
			);
		}

		return children;
	}
}

export { ErrorBoundary };
